import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { rgba } from 'polished'
import { graphql } from 'gatsby'
import {
  List,
  ListItem,
  Container,
  Section,
  Row,
  RowColumn,
  SectionTitle,
} from 'chaoskit/src/components'
import { text, misc, gradient } from 'chaoskit/src/assets/styles/utility'
import { isEmpty } from 'lodash'

import Data from '~components/Data'
import Quote from '~components/Quote'
import { textGradientBlockUnder } from '~styles/utility'
import BaseLayout from '~layouts/BaseLayout'
import ArticleFooter from '~partials/global/ArticleFooter'
import HelmetSEO from '~partials/global/HelmetSEO'
import Hero, { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import cloudinaryTransform from '~helpers/cloudinary'
import { CaseStudyHeroLogo, CaseStudyVideo } from '~components/case-studies/UI'

//
// @NOTE Various parts of this layout are split based on the existance of `frontmatter.video` which changes the "type" of page being displayed
//

const CaseStudyQuoteSection = ({ children, author, title, ...rest }) => {
  const theme = useTheme()

  return (
    <Quote
      author={author}
      title={title}
      contrastQuotes
      className="u-contrast"
      css={[
        misc.fluidSize({
          theme,
          from: theme.space.large,
          to: theme.space.large + theme.space.base,
          property: 'padding',
        }),
        {
          background: theme.brand.teal,
          boxShadow: theme.boxShadow.base,
          borderRadius: theme.borderRadius.base,

          blockquote: {
            fontWeight: theme.fontWeight.base,
          },

          '.G__Quote__Author': {
            color: `${theme.contrast.base} !important`,
          },

          '.G__Quote__Title': {
            color: `${theme.contrast.muted} !important`,
          },
        },
      ]}
      {...rest}
    >
      <Data>{children}</Data>
    </Quote>
  )
}

CaseStudyQuoteSection.propTypes = {
  children: PropTypes.string.isRequired,
  author: PropTypes.string,
  title: PropTypes.string,
}

const CaseStudyHeroLeftSideContent = ({ pageData }) => {
  const theme = useTheme()

  return (
    <Fragment>
      <CaseStudyHeroLogo
        title={pageData.title}
        src={pageData.hero.logo}
        css={[
          textGradientBlockUnder(theme, {
            start: theme.contrast.border,
            stop: theme.contrast.border,
          }),
        ]}
      />
      <HeroTitle css={{ marginTop: 0 }}>
        <Data>{pageData.hero.heading}</Data>
      </HeroTitle>
      <HeroTitleSub>
        <Data>{pageData.hero.blurb}</Data>
      </HeroTitleSub>
    </Fragment>
  )
}

CaseStudyHeroLeftSideContent.propTypes = {
  pageData: PropTypes.object.isRequired,
}

const CaseStudyDefaultHero = ({ pageData }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.space.large,

        [theme.mq.medium]: {
          gridTemplateColumns: '7fr 2px 5fr',
        },
      }}
    >
      <div>
        <CaseStudyHeroLeftSideContent pageData={pageData} />
      </div>
      <div
        css={{
          height: 2,
          background: `linear-gradient(to bottom, ${
            theme.contrast.muted
          }, ${rgba(theme.contrast.muted, 0)})`,
          display: 'block',

          [theme.mq.medium]: {
            height: '100%',
            width: 2,
          },
        }}
      />
      <div>
        <List space="large">
          {pageData.copy.results.map((result) => (
            <ListItem key={result.heading}>
              <div
                css={[
                  text.heading(theme),
                  {
                    textAlign: 'center',

                    [theme.mq.medium]: {
                      textAlign: 'left',
                    },
                  },
                ]}
              >
                <div css={[theme.fontSize.h3__fluid]}>
                  <Data>{result.heading}</Data>
                </div>
                <div
                  css={[
                    theme.fontSize.xlarge__fluid,
                    {
                      fontWeight: theme.fontWeight.base,
                    },
                  ]}
                >
                  <Data>{result.blurb}</Data>
                </div>
              </div>
            </ListItem>
          ))}
        </List>
      </div>
    </div>
  )
}

CaseStudyDefaultHero.propTypes = {
  pageData: PropTypes.object.isRequired,
}

const CaseStudyVideoHero = ({ pageData, pageContext }) => {
  const theme = useTheme()

  return (
    <Fragment>
      <div
        css={{
          display: 'grid',
          gap: theme.space.large,
          alignItems: 'center',

          [theme.mq.medium]: {
            gridTemplateColumns: '5fr 7fr',
            gap: 0,
          },
        }}
      >
        <div>
          <CaseStudyHeroLeftSideContent pageData={pageData} />
        </div>
        <div>
          <CaseStudyVideo
            url={pageData.video.url}
            teaserImage={pageData.video.teaserImage}
          />
        </div>
      </div>
      <CaseStudyFooter
        pageContext={pageContext}
        css={{ borderTop: `1px solid ${theme.contrast.border}` }}
      />
    </Fragment>
  )
}

CaseStudyVideoHero.propTypes = {
  pageData: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

const CaseStudyFooter = ({ pageContext: { previous, next }, ...rest }) => {
  const theme = useTheme()

  return (
    <ArticleFooter
      navigation={{
        previous: isEmpty(previous) ? null : previous,
        next: isEmpty(next) ? null : next,
      }}
      prevLabel="Previous Case Study"
      nextLabel="Next Case Study"
      homeLabel="Customers"
      homeUrl="/customers/"
      css={{
        paddingTop: theme.space.large,
        paddingBottom: theme.space.large,
      }}
      {...rest}
    />
  )
}

CaseStudyFooter.propTypes = {
  pageContext: PropTypes.object.isRequired,
}

const CaseStudyContent = ({ pageData, pageContext }) => {
  const theme = useTheme()

  return (
    <Container>
      <Section>
        <Data markdown>{pageData.copy.introContent}</Data>
        {pageData.copy?.quote && (
          <Quote
            author={pageData.copy.quote?.name}
            company={pageData.title}
            title={pageData.copy.quote?.title}
            size="large"
            isContainer
          >
            <Data>{pageData.copy.quote?.text}</Data>
          </Quote>
        )}
        <Data markdown>{pageData.copy.belowIntroContent}</Data>
      </Section>
      {pageData.copy?.challenges &&
        pageData.copy.challenges.map((challenge) => (
          <Section
            key={challenge.heading}
            css={{
              borderTop: theme.border.base,
            }}
          >
            <h5
              css={{
                color: theme.fontColor.muted,
                fontSize: theme.fontSize.large,
                marginBottom: theme.space.small,
                textAlign: 'center',
              }}
            >
              Challenge
            </h5>
            {challenge.heading && (
              <SectionTitle title={<Data>{challenge.heading}</Data>} />
            )}
            <Row gutter={{ base: 'large' }}>
              <RowColumn size={{ medium: 6 }}>
                <Data markdown>{challenge.content}</Data>
              </RowColumn>
              <RowColumn size={{ medium: 6 }}>
                <CaseStudyQuoteSection
                  author={challenge.quote.name}
                  title={challenge.quote.title}
                >
                  {challenge.quote.text}
                </CaseStudyQuoteSection>
              </RowColumn>
            </Row>
          </Section>
        ))}
      {pageData.copy?.solution && (
        <Section
          css={{
            borderTop: theme.border.base,
          }}
        >
          <h5
            css={{
              color: theme.fontColor.muted,
              fontSize: theme.fontSize.large,
              marginBottom: theme.space.small,
              textAlign: 'center',
            }}
          >
            The Solution
          </h5>
          {pageData.copy.solution.heading && (
            <SectionTitle
              title={<Data>{pageData.copy.solution.heading}</Data>}
            />
          )}
          <Data markdown>{pageData.copy.solution.content}</Data>
        </Section>
      )}
      {pageData.copy?.results && (
        <Section
          css={{
            borderTop: theme.border.base,
          }}
        >
          <SectionTitle title="Results" />
          <div
            css={{
              display: 'grid',
              gap: theme.space.large,
              alignItems: 'center',

              [theme.mq.medium]: {
                gridTemplateColumns: '5fr 7fr',
              },
            }}
          >
            {pageData.copy.results.map((result, index) => (
              <Fragment key={result.heading}>
                <div
                  css={[
                    text.heading(theme),
                    {
                      textAlign: 'center',
                      gridColumn: 1, // This is for some of the trickery 👇

                      [theme.mq.medium]: {
                        textAlign: 'left',
                      },
                    },
                  ]}
                >
                  <div css={[theme.fontSize.h3__fluid]}>
                    <Data>{result.heading}</Data>
                  </div>
                  <div
                    css={[
                      theme.fontSize.xlarge__fluid,
                      {
                        fontWeight: theme.fontWeight.base,
                      },
                    ]}
                  >
                    <Data>{result.blurb}</Data>
                  </div>
                </div>
                {result.quote && (
                  <div
                    css={[
                      // If we have two results, but only the first has a quote, we're assuming we can "stretch" this quote to both rows
                      // @NOTE This isn't scalable beyond two quotes, but we have ultimate control over the content here and it's something we can continue to build on in the future
                      pageData.copy.results.length === 2 &&
                        index === 0 &&
                        pageData.copy.results[0].quote &&
                        !pageData.copy.results[1].quote && {
                          [theme.mq.medium]: {
                            gridRow: '1 / span 2',
                            gridColumn: '2',
                          },
                        },
                    ]}
                  >
                    <CaseStudyQuoteSection
                      author={result.quote.name}
                      title={result.quote.title}
                    >
                      {result.quote.text}
                    </CaseStudyQuoteSection>
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </Section>
      )}
      <CaseStudyFooter
        pageContext={pageContext}
        css={{ borderTop: theme.border.base, marginTop: 0 }}
      />
    </Container>
  )
}

CaseStudyContent.propTypes = {
  pageData: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

const CaseStudyLayout = ({
  data: {
    pageData: { frontmatter: pageData },
  },
  pageContext,
}) => {
  const theme = useTheme()

  return (
    <BaseLayout>
      <HelmetSEO title={pageData.seoTitle} description={pageData.description} />
      <NodeTypeTracker nodeType={pageContext.nodeType} />

      <Hero
        css={[
          pageData.video?.url && {
            ...gradient.dark(theme),
          },

          !pageData.video?.url && {
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',

            '&::after': {
              content: "''",
              position: 'absolute',
              zIndex: 1,
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: rgba(theme.color.dark.base, 0.75),
            },
          },
        ]}
        style={{
          backgroundImage: pageData.hero.background
            ? `url(${cloudinaryTransform(pageData.hero.background)})`
            : null,
        }}
      >
        <HeroContent>
          <Container>
            {pageData.video?.url ? (
              <CaseStudyVideoHero
                pageData={pageData}
                pageContext={pageContext}
              />
            ) : (
              <CaseStudyDefaultHero pageData={pageData} />
            )}
          </Container>
        </HeroContent>
      </Hero>
      {pageData.video?.url ? null : (
        <CaseStudyContent pageData={pageData} pageContext={pageContext} />
      )}
    </BaseLayout>
  )
}

CaseStudyLayout.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object,
}

export default CaseStudyLayout

export const pageQuery = graphql`
  query CaseStudySingle($slug: String!) {
    pageData: mdx(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/case-studies/" }
    ) {
      fileAbsolutePath
      frontmatter {
        title
        seoTitle
        description
        slug
        hero {
          background
          logo
          heading
          blurb
        }
        video {
          url
          teaserImage
        }
        copy {
          introContent
          quote {
            text
            name
            title
          }
          belowIntroContent
          challenges {
            heading
            content
            quote {
              text
              name
              title
            }
          }
          solution {
            heading
            content
          }
          results {
            heading
            blurb
            quote {
              text
              name
              title
            }
          }
        }
      }
      fields {
        slug
      }
    }
  }
`
